import React, { useCallback, useContext, useMemo } from 'react'
import { Dropdown, List, Avatar } from 'antd'
import { LogoutOutlined, HomeOutlined, TeamOutlined } from '@ant-design/icons'
import { useIdleTimer } from 'react-idle-timer'
import { useMsal } from '@azure/msal-react'
import { signOut } from 'aws-amplify/auth'
import { clearStorage } from '../../utils/storageUtils'
import { clearAllUserData, emergencyCleanup } from '../../api/authApiService'
import { UserContext } from '../../context/userContext'
import { StoreContext } from '../../context/storeContext'
import { generateAvatarProps } from '../../utils/functionUtils'
import styles from './userPanel.module.scss'

const UserPanel = ({ menuMode = false, forceUpperCase = false }) => {
  const {
    graphData,
    profileImageData,
    cognitoData,
    userRole,
    userStores,
    logoutUser
  } = useContext(UserContext)

  // Get the logout function from StoreContext (which includes comprehensive cleanup)
  const { name: storeName, logout: storeContextLogout } = useContext(StoreContext)

  const { instance } = useMsal()
  const loginType = localStorage.getItem('loginType')
  const timeout = 2 * 60 * 60 * 1000
  const promptBeforeIdle = 1.8 * 60 * 60 * 1000

  const handleLogoutPopup = useCallback(async () => {
    try {
      console.log('Starting logout process from UserPanel...')

      // Use the comprehensive logout from StoreContext which already includes
      // UserContext logout and all cleanup functionality
      if (storeContextLogout) {
        await storeContextLogout()
        return // StoreContext logout handles everything including redirect
      }

      // Fallback manual logout if StoreContext logout is not available
      console.log('Fallback to manual logout process...')

      // Clear user context if available
      if (logoutUser) {
        logoutUser()
      }

      // Clear all user access cache and application data
      clearAllUserData()

      // Handle logout based on login type
      if (loginType === 'cognito' && cognitoData) {
        try {
          await signOut({ global: true }) // Sign out globally from all devices
          console.log('Cognito logout successful')
        } catch (error) {
          console.error('Error logging out from Cognito:', error)
        }

        // Clear any remaining Cognito-specific data
        localStorage.clear()
        sessionStorage.clear()
        window.location.replace('/login')

      } else {
        // Microsoft AD logout
        const account = instance.getActiveAccount()

        // Clear storage using the existing utility
        clearStorage(account)

        // Clear MSAL cache
        if (instance) {
          try {
            // Clear all accounts from MSAL cache
            const accounts = instance.getAllAccounts()
            accounts.forEach(account => {
              instance.removeAccount(account)
            })

            // Clear the token cache
            await instance.clearCache()
            console.log('MSAL cache cleared')
          } catch (error) {
            console.error('Error clearing MSAL cache:', error)
          }
        }

        // Additional cleanup before redirect
        localStorage.clear()
        sessionStorage.clear()

        // Perform MSAL logout redirect
        instance.logoutRedirect({
          mainWindowRedirectUri: '/',
          account: instance.getActiveAccount(),
          postLogoutRedirectUri: window.location.origin
        })
      }

      // Additional cleanup - ensure no residual data remains
      setTimeout(() => {
        localStorage.clear()
        sessionStorage.clear()
      }, 100)

    } catch (error) {
      console.error('Error during logout process:', error)

      // Emergency fallback
      try {
        emergencyCleanup()
        window.location.href = '/login'
      } catch (emergencyError) {
        console.error('Emergency cleanup also failed:', emergencyError)
        // Last resort - force navigation
        window.location.href = '/login'
      }
    }
  }, [instance, cognitoData, loginType, logoutUser, storeContextLogout])

  const onIdle = () => {
    const choice = window.confirm(
      'You\'ve been idle for some time. Do you want to stay logged in?'
    )
    if (choice) {
      window.location.reload()
      activate()
    } else {
      handleLogoutPopup()
    }
  }

  const { activate } = useIdleTimer({
    onIdle,
    timeout,
    promptBeforeIdle,
    throttle: 500,
    crossTab: true,
    leaderElection: true,
    syncTimers: 200
  })

  // Format the roles for display
  const roleDisplay = useMemo(() => {
    // For Cognito users, always display "Store Assistant"
    if (loginType === 'cognito') {
      return 'Store Assistant'
    }

    if (!userRole) {
      return 'No roles assigned'
    }

    // If there are multiple roles, join them with commas
    return userRole
  }, [userRole, loginType])

  // Get the store display information - Updated to use current store from context
  const storeDisplay = useMemo(() => {
    // Always show the current store name from StoreContext if available
    if (storeName) {
      return storeName
    }

    // Fallback logic for when no store is selected
    if (loginType === 'cognito') {
      return 'No store assigned'
    }

    // For Microsoft AD users, check the stores from userStores
    if (loginType === 'microsoft') {
      const accessibleStores = userStores || []

      if (accessibleStores.length === 0) {
        return 'No store assigned'
      }

      // If there's only one store, display it
      if (accessibleStores.length === 1) {
        const storeInfo = accessibleStores[0]
        return typeof storeInfo === 'object'
          ? (storeInfo.storeName || storeInfo.name || storeInfo.id)
          : storeInfo
      }

      // If multiple stores but no current selection
      const firstStore = accessibleStores[0]
      const firstStoreName = typeof firstStore === 'object'
        ? (firstStore.storeName || firstStore.name || firstStore.id)
        : firstStore

      return `${firstStoreName} +${accessibleStores.length - 1} more`
    }

    return 'No store assigned'
  }, [storeName, loginType, userStores])

  const menuItems = useMemo(() => {
    const items = []

    // Add email information as the first item in dropdown for mobile view
    const displayEmail = loginType === 'cognito'
      ? (forceUpperCase || loginType === 'cognito' ? cognitoData?.username?.toUpperCase() : cognitoData?.username)
      : (graphData?.mail || '')

    items.push({
      key: 'email',
      label: displayEmail,
      className: styles.menuEmail
    })

    // Add role information as a clickable item with icon
    items.push({
      key: 'role',
      label: roleDisplay,
      icon: <TeamOutlined />,
      className: styles.menuItem
    })

    // Add store information for both login types - now uses current store from context
    items.push({
      key: 'store',
      label: storeDisplay,
      icon: <HomeOutlined />,
      className: styles.menuItem
    })

    items.push({
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogoutPopup,
      className: styles.menuItem
    })

    return items
  }, [handleLogoutPopup, roleDisplay, storeDisplay, loginType, cognitoData, graphData, forceUpperCase])

  // Determine the display name based on login type
  const displayName = loginType === 'cognito'
    ? cognitoData?.username
    : (graphData?.displayName || graphData?.mail)

  // Check if user has a profile photo
  const hasProfilePhoto = loginType === 'microsoft' && profileImageData?.photo

  // Format display name for the UI - use email for display
  const formattedDisplayName = loginType === 'cognito'
    ? (forceUpperCase || loginType === 'cognito' ? cognitoData?.username?.toUpperCase() : cognitoData?.username)
    : (graphData?.mail || '')

  // Create the profile image component
  const profileImageComponent = hasProfilePhoto ? (
    <div
      style={{
        backgroundImage: `url(${profileImageData.photo})`,
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#fff',
        backgroundSize: 'cover',
        borderRadius: '50%'
      }}
      className={styles.userImage}
    />
  ) : (
    <Avatar
      {...generateAvatarProps(displayName)}
      style={{
        width: 29,
        height: 29,
        margin: 'auto',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'white',
        color: 'black',
        justifyContent: 'center'
      }}
    />
  )

  const userInfo = (
    <div className={styles.userInfo}>
      {/* Always include the userName div, but styling will handle visibility */}
      <div className={styles.userName}>
        {formattedDisplayName}
      </div>
      <div className={styles.imageContainer}>
        {profileImageComponent}
      </div>
    </div>
  )

  return (
    <div className={styles.userPanel}>
      {menuMode === 'context' ? (
        <Dropdown
          menu={{ items: menuItems }}
          placement="bottomRight"
          trigger={['click']}
          overlayClassName={styles.userMenu}
        >
          {userInfo}
        </Dropdown>
      ) : (
        <>
          {userInfo}
          <List
            className={styles.menuList}
            dataSource={menuItems}
            renderItem={item => (
              <List.Item
                onClick={item.onClick}
                className={item.className}
              >
                {item.icon}
                <span>{item.label}</span>
              </List.Item>
            )}
          />
        </>
      )}
    </div>
  )
}

export default UserPanel